import { ref, onValue, set, get, update } from 'firebase/database';
import { database } from '../config/firebase';
import { GameSession, Player, Definition, Round } from '../types';
import { getNextWord } from './wordService';

export const gameService = {
  // Test database connection
  async testConnection(): Promise<boolean> {
    try {
      const connRef = ref(database, '.info/connected');
      return new Promise((resolve) => {
        onValue(connRef, (snap) => {
          resolve(snap.val() === true);
        });
      });
    } catch (error) {
      console.error('Error testing connection:', error);
      return false;
    }
  },

  // Create a new game
  async createGame(readerId: string, readerName: string): Promise<string> {
    try {
      const gameId = Math.random().toString(36).substring(2, 8).toUpperCase();
      const reader: Player = {
        id: readerId,
        name: readerName,
        score: 0,
        isReader: true,
      };

      const initialGameState: GameSession = {
        id: gameId,
        state: {
          round: 1,
          players: [reader],
          currentReader: reader,
          readerStatus: 'idle',
          phase: 'lobby',
          wordBankId: 'jamaican',
          usedWords: [],
          mode: 'individual',
          ruleSet: {
            maxPlayers: 8,
            minPlayers: 3,
            roundTimeLimit: 120,
            maxRounds: 10,
            scoreSystem: 'standard'
          },
          teams: []
        },
        currentRound: {
          word: '',
          definitions: [],
          correctDefinition: '',
          phase: 'writing',
        },
        isActive: true,
        readerId,
      };

      await set(ref(database, `games/${gameId}`), initialGameState);
      return gameId;
    } catch (error) {
      console.error('Error creating game:', error);
      throw error;
    }
  },

  // Verify if a game exists and is active
  async verifyGame(gameId: string): Promise<boolean> {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      const game = snapshot.val() as GameSession;
      return game && game.isActive;
    } catch (error) {
      console.error('Error verifying game:', error);
      return false;
    }
  },

  // Join an existing game
  async joinGame(gameId: string, player: Player): Promise<void> {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      const game = snapshot.val() as GameSession;

      if (!game || !game.isActive) {
        throw new Error('Game not found or no longer active');
      }

      if (game.state.phase !== 'lobby') {
        throw new Error('Game has already started');
      }

      const updatedPlayers = [...game.state.players, player];
      await update(ref(database, `games/${gameId}/state`), {
        players: updatedPlayers,
      });
    } catch (error) {
      console.error('Error joining game:', error);
      throw error;
    }
  },

  // Start the game
  async startGame(gameId: string): Promise<void> {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      const game = snapshot.val() as GameSession;

      if (!game || !game.isActive) {
        throw new Error('Game not found or no longer active');
      }

      // Get the first word
      const wordEntry = await getNextWord(game.state.wordBankId);

      const updatedRound: Round = {
        word: wordEntry.word,
        definitions: [],
        correctDefinition: wordEntry.definition,
        phase: 'writing',
      };

      await update(ref(database, `games/${gameId}`), {
        'state/phase': 'writing',
        currentRound: updatedRound,
      });
    } catch (error) {
      console.error('Error starting game:', error);
      throw error;
    }
  },

  // Submit a definition
  async submitDefinition(gameId: string, definition: Definition): Promise<void> {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      const game = snapshot.val() as GameSession;

      if (!game || !game.isActive) {
        throw new Error('Game not found or no longer active');
      }

      // Ensure the definition has a votes array initialized
      const definitionWithVotes = {
        ...definition,
        votes: definition.votes || []
      };

      const updatedDefinitions = [...(game.currentRound.definitions || []), definitionWithVotes];
      await update(ref(database, `games/${gameId}/currentRound`), {
        definitions: updatedDefinitions,
      });
    } catch (error) {
      console.error('Error submitting definition:', error);
      throw error;
    }
  },

  // Submit a vote
  async submitVote(gameId: string, definitionId: string, voterId: string): Promise<void> {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      const game = snapshot.val() as GameSession;

      if (!game || !game.isActive) {
        throw new Error('Game not found or no longer active');
      }

      if (!game.currentRound?.definitions) {
        throw new Error('No definitions available to vote on');
      }

      // First, remove the voter's vote from all definitions
      const updatedDefinitions = (game.currentRound.definitions || []).map(def => ({
        ...def,
        votes: Array.isArray(def.votes) ? def.votes.filter(id => id !== voterId) : []
      }));

      // Then, add the vote to the selected definition
      // Note: We allow voting for own definition, but scoring will handle this separately
      const finalDefinitions = updatedDefinitions.map(def => {
        if (def.authorId === definitionId) {
          const currentVotes = Array.isArray(def.votes) ? def.votes : [];
          // Allow self-voting (scoring will handle this separately)
          return {
            ...def,
            votes: [...currentVotes, voterId]
          };
        }
        return def;
      });

      // Log the vote counts and self-votes for debugging
      console.log('Vote counts:', finalDefinitions.map(def => ({
        authorId: def.authorId,
        voteCount: def.votes?.length || 0,
        selfVote: def.authorId === voterId && def.votes?.includes(voterId)
      })));

      await update(ref(database, `games/${gameId}/currentRound`), {
        definitions: finalDefinitions,
      });
    } catch (error) {
      console.error('Error submitting vote:', error);
      throw error;
    }
  },

  // Start a new round
  async startNewRound(gameId: string): Promise<void> {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      const game = snapshot.val() as GameSession;

      if (!game || !game.isActive) {
        throw new Error('Game not found or no longer active');
      }

      // Get next word
      const wordEntry = await getNextWord(
        game.state.wordBankId,
        game.state.currentDifficulty,
        game.state.currentCategory
      );

      const newRound: Round = {
        word: wordEntry.word,
        definitions: [], // Empty definitions array
        correctDefinition: wordEntry.definition,
        phase: 'writing'
      };

      // Update game state with new round
      await update(ref(database, `games/${gameId}`), {
        'state/round': game.state.round + 1,
        'state/phase': 'writing',
        currentRound: newRound
      });

      // Also update any player-specific round state
      const updatedPlayers = game.state.players.map(player => ({
        ...player,
        hasSubmittedDefinition: false // Reset submission state
      }));

      await update(ref(database, `games/${gameId}/state`), {
        players: updatedPlayers
      });

    } catch (error) {
      console.error('Error starting new round:', error);
      throw error;
    }
  },

  // Calculate and update scores for the current round
  async calculateAndUpdateScores(gameId: string): Promise<void> {
    try {
      const gameRef = ref(database, `games/${gameId}`);
      const snapshot = await get(gameRef);
      const game = snapshot.val() as GameSession;

      if (!game || !game.isActive) {
        throw new Error('Game not found or no longer active');
      }

      const { currentRound, state } = game;
      const updatedPlayers = [...state.players];

      // Find the correct definition (either marked as correct or has authorId 'correct')
      const correctDefinition = currentRound.definitions.find(
        def => def.isCorrect || def.authorId === 'correct'
      );
      
      if (!correctDefinition) {
        console.error('No correct definition found');
        return;
      }

      // Calculate scores for each player
      updatedPlayers.forEach(player => {
        let roundScore = 0;

        // Reader gets 0 points
        if (player.isReader) {
          roundScore = 0;
        } else {
          // Handle non-reader scores
          
          // Get the player's definition
          const playerDef = currentRound.definitions.find(def => def.authorId === player.id);
          
          if (playerDef) {
            // 2 points for choosing the correct definition
            if (correctDefinition.votes?.includes(player.id)) {
              roundScore += 2;
              console.log(`${player.name} got 2 points for choosing correct definition`);
            }

            // 1 point for each player who picks their made-up definition (excluding self-votes)
            const votesForPlayer = playerDef.votes?.filter(voterId => {
              // Exclude self-votes and reader votes
              const voter = state.players.find(p => p.id === voterId);
              return voter && !voter.isReader && voterId !== player.id;
            }) || [];
            
            roundScore += votesForPlayer.length;
            if (votesForPlayer.length > 0) {
              console.log(`${player.name} got ${votesForPlayer.length} points for fooling others`);
            }
          }
        }

        // Update player's score
        player.score = (player.score || 0) + roundScore;
        console.log(`${player.name}'s final score: ${player.score} (added ${roundScore} points)`);
      });

      // Update the database with new scores
      await update(ref(database, `games/${gameId}/state`), {
        players: updatedPlayers
      });

    } catch (error) {
      console.error('Error calculating scores:', error);
      throw error;
    }
  },

  // Update game phase
  async updateGamePhase(gameId: string, phase: 'writing' | 'voting' | 'results' | 'reviewing'): Promise<void> {
    try {
      // If moving to results phase, calculate scores first
      if (phase === 'results') {
        await this.calculateAndUpdateScores(gameId);
      }

      await update(ref(database, `games/${gameId}`), {
        'state/phase': phase,
        'currentRound/phase': phase,
      });
    } catch (error) {
      console.error('Error updating game phase:', error);
      throw error;
    }
  },

  // Update reader status
  async updateReaderStatus(gameId: string, status: 'typing' | 'idle' | 'reading' | 'waiting'): Promise<void> {
    try {
      await update(ref(database, `games/${gameId}/state`), {
        readerStatus: status,
      });
    } catch (error) {
      console.error('Error updating reader status:', error);
      throw error;
    }
  },

  // Update game settings
  async updateGameSettings(gameId: string, settings: {
    wordBankId: string;
    difficulty?: 'easy' | 'medium' | 'hard';
    category?: string;
  }): Promise<void> {
    try {
      await update(ref(database, `games/${gameId}/state`), {
        wordBankId: settings.wordBankId,
        currentDifficulty: settings.difficulty,
        currentCategory: settings.category,
      });
    } catch (error) {
      console.error('Error updating game settings:', error);
      throw error;
    }
  },

  // Update definitions
  async updateDefinitions(gameId: string, definitions: Definition[], allReviewed?: boolean): Promise<void> {
    try {
      await update(ref(database, `games/${gameId}/currentRound`), {
        definitions,
        allDefinitionsReviewed: allReviewed,
      });
    } catch (error) {
      console.error('Error updating definitions:', error);
      throw error;
    }
  },

  // End game
  async endGame(gameId: string): Promise<void> {
    try {
      await update(ref(database, `games/${gameId}`), {
        isActive: false,
      });
    } catch (error) {
      console.error('Error ending game:', error);
      throw error;
    }
  },

  // Set up real-time game updates listener
  onGameUpdate(gameId: string, callback: (game: GameSession | null) => void) {
    const gameRef = ref(database, `games/${gameId}`);
    const unsubscribe = onValue(gameRef, (snapshot) => {
      const game = snapshot.val() as GameSession;
      callback(game || null);
    });
    return unsubscribe;
  },
}; 